import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { AlertCircle, Eye, EyeOff, LoaderCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { authErrorMessage } from '../lib/authErrorMessage'
import { authFeatureService } from '../services/auth.feature.service'
import type { SignUpFormValues } from '../types'

export function SignUpForm({ returnTo = '/dashboard' }: { returnTo?: string }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [googleError, setGoogleError] = useState<string | null>(null)
  const [passwordVisible, setPasswordVisible] = useState(false)
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<SignUpFormValues>({ mode: 'onBlur' })
  const busy = loading || googleLoading

  function clearError() {
    setError(null)
    setGoogleError(null)
  }

  async function handleGoogleSignUp() {
    clearError()
    setGoogleLoading(true)

    try {
      await authFeatureService.signInWithGoogle()
      navigate(returnTo, { replace: true })
    } catch (err) {
      setGoogleError(authErrorMessage(err, 'google-sign-in'))
    } finally {
      setGoogleLoading(false)
    }
  }

  async function handleSignUp(values: SignUpFormValues) {
    setLoading(true)
    setError(null)
    setGoogleError(null)

    try {
      await authFeatureService.signUp({
        ...values,
        displayName: values.displayName.trim(),
        email: values.email.trim(),
      })
      navigate(returnTo, { replace: true })
    } catch (err) {
      setError(authErrorMessage(err, 'sign-up'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <Button
        type="button"
        variant="outline"
        className="google-sign-in-button h-11 w-full rounded-full shadow-xs"
        disabled={busy}
        aria-busy={googleLoading}
        onClick={() => void handleGoogleSignUp()}
      >
        {googleLoading ? (
          <LoaderCircle
            className="size-5 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
        ) : (
          <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        )}
        {googleLoading ? 'Opening Google…' : 'Sign up with Google'}
      </Button>

      {googleError && (
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertDescription>{googleError}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-3" aria-hidden="true">
        <Separator className="flex-1" />
        <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
          or use email
        </span>
        <Separator className="flex-1" />
      </div>

      <form
        onSubmit={(event) => void handleSubmit(handleSignUp)(event)}
        className="space-y-5"
        noValidate
        aria-busy={busy}
      >
        <div className="space-y-2">
          <Label htmlFor="displayName">Full name</Label>
          <Input
            id="displayName"
            type="text"
            autoComplete="name"
            className="h-11 rounded-lg bg-background/70"
            aria-invalid={Boolean(errors.displayName)}
            aria-describedby={errors.displayName ? 'display-name-error' : undefined}
            {...register('displayName', {
              required: 'Enter your full name.',
              validate: (value) => value.trim().length >= 2 || 'Enter your full name.',
              onChange: clearError,
            })}
          />
          {errors.displayName && (
            <p id="display-name-error" className="text-sm text-destructive">
              {errors.displayName.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="sign-up-email">Email address</Label>
          <Input
            id="sign-up-email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            className="h-11 rounded-lg bg-background/70"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'sign-up-email-error' : undefined}
            {...register('email', {
              required: 'Enter your email address.',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address.',
              },
              onChange: clearError,
            })}
          />
          {errors.email && (
            <p id="sign-up-email-error" className="text-sm text-destructive">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="sign-up-password">Password</Label>
          <div className="relative">
            <Input
              id="sign-up-password"
              type={passwordVisible ? 'text' : 'password'}
              autoComplete="new-password"
              className="h-11 rounded-lg bg-background/70 pr-11"
              aria-invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? 'sign-up-password-error' : 'password-hint'}
              {...register('password', {
                required: 'Create a password.',
                minLength: {
                  value: 8,
                  message: 'Use at least 8 characters.',
                },
                onChange: clearError,
              })}
            />
            <button
              type="button"
              className="absolute top-0 right-0 flex h-11 w-11 items-center justify-center rounded-r-lg text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              aria-label={passwordVisible ? 'Hide password' : 'Show password'}
              aria-pressed={passwordVisible}
              onClick={() => setPasswordVisible((visible) => !visible)}
            >
              {passwordVisible ? (
                <EyeOff className="size-4" aria-hidden="true" />
              ) : (
                <Eye className="size-4" aria-hidden="true" />
              )}
            </button>
          </div>
          {errors.password ? (
            <p id="sign-up-password-error" className="text-sm text-destructive">
              {errors.password.message}
            </p>
          ) : (
            <p id="password-hint" className="text-sm text-muted-foreground">
              Use at least 8 characters.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <Input
            id="confirmPassword"
            type={passwordVisible ? 'text' : 'password'}
            autoComplete="new-password"
            className="h-11 rounded-lg bg-background/70"
            aria-invalid={Boolean(errors.confirmPassword)}
            aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
            {...register('confirmPassword', {
              required: 'Confirm your password.',
              validate: (value) => value === getValues('password') || 'Passwords do not match.',
              onChange: clearError,
            })}
          />
          {errors.confirmPassword && (
            <p id="confirm-password-error" className="text-sm text-destructive">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle aria-hidden="true" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" size="lg" className="h-11 w-full rounded-full" disabled={busy}>
          {loading && (
            <LoaderCircle
              className="size-5 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          )}
          {loading ? 'Creating your account…' : 'Create account'}
        </Button>
      </form>

      <p className="border-t border-border/70 pt-5 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link
          to="/sign-in"
          state={{ returnTo }}
          className="font-medium text-foreground underline underline-offset-4"
        >
          Sign in
        </Link>
      </p>
    </div>
  )
}
